import { ActionConfirmation, usePublishActionOutcome, type ActionOutcome } from "./ui/ActionConfirmation.js";
import { useEffect, useId, useState } from "react";
import { isOwner, useAuth } from "./auth.js";
import {
  addStudentToLesson,
  ApiError,
  correctLessonStudentName,
  lessonStudentChoices,
  moveLessonToStudent,
  type LessonMoved,
  type LessonStudentChoices,
} from "./api.js";

/**
 * "This isn't the child I just taught."
 *
 * Attribution attaches a booking to a student by guessing from who paid, and
 * where a family books two children on one card it sometimes guesses wrong —
 * so a guitar lesson arrives in the diary wearing an older brother's name, and
 * the notes written on it are filed against him and emailed to whoever receives
 * his. The person who can see that is the tutor in the room, and until now the
 * only cure was somebody editing the database by hand.
 *
 * So it lives here, on the write-up page, which is where a tutor already is at
 * the moment they notice. Closed until asked for: the name at the top of a
 * lesson is right almost every time, and a picker sitting open beside it
 * invites a mis-click on a screen whose actual job is the note.
 * A wrong name for the same learner is a separate owner edit: changing the
 * learner id for that case would split their past and future history.
 */

/**
 * The select's value for "nobody on this list".
 *
 * A sentinel rather than a second control, so the question stays one question:
 * who was this lesson for, with adding somebody as the last answer rather than
 * a separate mode to be in.
 */
const NEW = "__new";

function lessonMovedOutcome(done: LessonMoved): ActionOutcome {
  const moved = done.moved === 1
    ? `Moved to ${done.student.fullName}.`
    : `${done.moved} lessons moved to ${done.student.fullName}.`;
  return {
    variant: done.reachable === false || done.notesAlreadySent > 0 ? "attention" : "success",
    title: "Lesson student changed",
    description: done.student.fullName,
    details: [
      ...(done.created ? [{ value: `${done.student.fullName} added to the account.` }] : []),
      { value: moved },
      ...(done.notesMoved > 0 ? [{ value: done.notesMoved === 1 ? "The note moved with the lesson." : `${done.notesMoved} notes moved with the lessons.` }] : []),
      ...(done.reachable === false ? [{ value: "This student has no email address on their record, so lesson notes will not reach anybody until an owner adds one." }] : []),
      ...(done.notesAlreadySent > 0 ? [{ value: done.notesAlreadySent === 1 ? "One note had already been emailed to the previous family record and cannot be unsent." : `${done.notesAlreadySent} notes had already been emailed to the previous family record and cannot be unsent.` }] : []),
      ...(done.skipped > 0 ? [{ value: done.skipped === 1 ? "One lesson was already assigned to this student and was left alone." : `${done.skipped} lessons were already assigned to this student and were left alone.` }] : []),
      ...(done.notesLeft > 0 ? [{ value: done.notesLeft === 1 ? "One note stayed where it was because this student already has a write-up for that lesson." : `${done.notesLeft} notes stayed where they were because this student already has write-ups for those lessons.` }] : []),
    ],
  };
}

interface Props {
  bookingId: string;
  /** Who the lesson is on now, or null when nothing has claimed it. */
  studentName: string | null;
  /** Re-reads the page, so the heading and the note follow the lesson. */
  onMoved: () => void;
  /** Used by the Diary, which has already asked to match this booking. */
  embedded?: boolean;
  onBusyChange?: (busy: boolean) => void;
}

export function MoveLesson({ bookingId, studentName, onMoved, embedded = false, onBusyChange }: Props) {
  // Only to label the long group honestly. An owner's list is the whole school,
  // and calling that "your students" reads as a bug to the person who knows it
  // is not their teaching list.
  const { me } = useAuth();
  const publish = usePublishActionOutcome();
  const choiceGroup = useId();
  const [choices, setChoices] = useState<LessonStudentChoices | null>(null);
  const [open, setOpen] = useState(embedded);
  const [loading, setLoading] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [studentId, setStudentId] = useState("");
  const [scope, setScope] = useState<"one" | "series">("one");
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [done, setDone] = useState<ActionOutcome | null>(null);
  const [intent, setIntent] = useState<"name" | "student" | null>(null);
  const [correctedName, setCorrectedName] = useState("");
  /** The name being added, when the answer is nobody on the list. */
  const [newName, setNewName] = useState("");
  /**
   * Set when the server asked whether a similar name is the same child.
   *
   * Kept as its own state rather than folded into `problem`, because it is a
   * question with a button under it and not a failure: the next press means
   * "yes, add them anyway".
   */
  const [askAnyway, setAskAnyway] = useState<string | null>(null);

  useEffect(() => {
    setChoices(null);
    setOpen(embedded);
    setLoading(false);
    setLoadAttempt(0);
    setStudentId("");
    setScope("one");
    setProblem(null);
    setDone(null);
    setIntent(null);
    setCorrectedName("");
    setNewName("");
    setAskAnyway(null);
  }, [bookingId, embedded]);

  // Fetched when the question is asked rather than with the page. Every lesson
  // write-up would otherwise pay for a list nobody looks at.
  useEffect(() => {
    if (!open || choices !== null) return;
    let current = true;
    setProblem(null);
    setLoading(true);
    void lessonStudentChoices(bookingId).then((result) => {
      if (!current) return;
      setChoices(result);
      setCorrectedName(result.booking.studentName ?? "");
      setLoading(false);
    }).catch((cause: unknown) => {
      if (!current) return;
      setProblem(cause instanceof Error ? cause.message : "Could not load the students.");
      setLoading(false);
    });
    return () => { current = false; };
  }, [open, bookingId, choices, loadAttempt]);

  const addingSomebodyNew = studentId === NEW;

  function showOutcome(outcome: ActionOutcome) {
    if (!publish(outcome)) setDone(outcome);
    else setDone(null);
    setOpen(false);
    // The same booking can now carry a different name or student. Reopening
    // must read that identity afresh rather than reuse the old picker.
    setChoices(null);
    setIntent(null);
    setStudentId("");
    setScope("one");
    setNewName("");
    setAskAnyway(null);
    onMoved();
  }

  async function move() {
    if (studentId === "") return;
    if (addingSomebodyNew && newName.trim() === "") return;

    setSaving(true);
    onBusyChange?.(true);
    setProblem(null);
    try {
      const result = addingSomebodyNew
        ? await addStudentToLesson(bookingId, newName.trim(), scope, askAnyway !== null)
        : await moveLessonToStudent(bookingId, studentId, scope);
      showOutcome(lessonMovedOutcome(result));
    } catch (cause: unknown) {
      // "Is this the same person?" is a question, and pressing the button again
      // is the answer. Anything else is a refusal and reads as one.
      if (cause instanceof ApiError && cause.code === "student_similar") {
        setAskAnyway(cause.message);
      } else {
        setAskAnyway(null);
        setProblem(cause instanceof Error ? cause.message : "Could not move the lesson.");
      }
    } finally {
      setSaving(false);
      onBusyChange?.(false);
    }
  }

  async function correctName() {
    if (
      !isOwner(me) || !choices?.booking.studentId ||
      !choices.booking.studentName || !correctedName.trim()
    ) return;
    setSaving(true);
    onBusyChange?.(true);
    setProblem(null);
    try {
      const result = await correctLessonStudentName(bookingId, {
        studentId: choices.booking.studentId,
        expectedName: choices.booking.studentName,
        fullName: correctedName.trim(),
      });
      const outcome: ActionOutcome = {
        variant: "success",
        title: "Student name corrected",
        description: result.student.fullName,
        details: [{ value: "All past and upcoming lessons, notes and practice remain on the same student record." }],
      };
      showOutcome(outcome);
    } catch (cause: unknown) {
      setProblem(cause instanceof Error ? cause.message : "Could not correct the name.");
    } finally {
      setSaving(false);
      onBusyChange?.(false);
    }
  }

  if (done) {
    return <ActionConfirmation {...done} reveal />;
  }

  if (!open) {
    return (
      <p className="small">
        <button type="button" className="link" onClick={() => setOpen(true)}>
          {studentName === null ? "Say who this lesson is for" : `Not ${studentName}?`}
        </button>
      </p>
    );
  }

  const candidates = (choices?.candidates ?? []).filter(
    (candidate) => candidate.id !== choices?.booking.studentId,
  );
  const family = candidates.filter((candidate) => candidate.matchedOn === "booker");
  const rest = candidates.filter((candidate) => candidate.matchedOn === "taught");
  const series = choices?.series;
  const account = choices?.booking.customerName ?? "the same person";
  const hasStudent = choices?.booking.studentId != null;
  const movingStudent = !hasStudent || intent === "student";

  return (
    <div className="panel">
      <div className="spread">
        <strong>Correct the student on this lesson</strong>
        {!embedded && <button type="button" className="quiet small" disabled={saving} onClick={() => setOpen(false)}>
          Close
        </button>}
      </div>

      {problem && <div className="error small" style={{ marginTop: 10 }}>{problem}</div>}
      {loading && <p className="small muted">Loading…</p>}
      {problem && !loading && choices === null && (
        <button type="button" className="quiet small" onClick={() => setLoadAttempt((attempt) => attempt + 1)}>
          Try again
        </button>
      )}

      {choices && (
        <>
          {hasStudent && (
            <fieldset disabled={saving} style={{ border: 0, padding: 0, margin: "12px 0" }}>
              <legend className="small muted">What needs correcting?</legend>
              <label className="row" style={{ marginTop: 6 }}>
                <input
                  type="radio"
                  name={`${choiceGroup}-intent`}
                  checked={intent === "name"}
                  onChange={() => { setIntent("name"); setProblem(null); setAskAnyway(null); }}
                />
                <span className="small">Same student — correct their name</span>
              </label>
              <label className="row" style={{ marginTop: 6 }}>
                <input
                  type="radio"
                  name={`${choiceGroup}-intent`}
                  checked={intent === "student"}
                  onChange={() => { setIntent("student"); setProblem(null); setAskAnyway(null); }}
                />
                <span className="small">Different student — move lessons</span>
              </label>
            </fieldset>
          )}

          {hasStudent && intent === "name" && (
            isOwner(me) ? <>
              <label style={{ display: "block", marginTop: 12 }}>
                <span className="small muted">Correct student name</span>
                <input
                  value={correctedName}
                  disabled={saving}
                  maxLength={200}
                  onChange={(event) => setCorrectedName(event.target.value)}
                  style={{ display: "block", marginTop: 4, width: "100%", maxWidth: 320 }}
                />
              </label>
              <p className="small">
                This updates {choices.booking.studentName}&rsquo;s name on their existing record.
                All past and upcoming lessons, notes and practice stay together.
                Parent and payment details stay as recorded.
              </p>
              <button
                type="button"
                disabled={saving || !correctedName.trim() || correctedName.trim() === choices.booking.studentName}
                onClick={() => void correctName()}
              >
                {saving ? "Saving…" : "Save corrected name"}
              </button>
            </> : <p className="small notice">
              An owner can correct this student&rsquo;s name while keeping their full history together.
              Ask an owner to update {choices.booking.studentName}&rsquo;s record.
            </p>
          )}

          {movingStudent && <>
            <p className="small">Who was this lesson for?</p>
            <label style={{ display: "block", marginTop: 12 }}>
              <span className="small muted">Student</span>
              <select
                disabled={saving}
                value={studentId}
                onChange={(e) => {
                  setStudentId(e.target.value);
                  // A different answer is a different question: whatever the
                  // server asked about the last name typed no longer applies.
                  setAskAnyway(null);
                  setProblem(null);
                }}
                style={{ display: "block", marginTop: 4 }}
              >
                <option value="">Choose somebody</option>
                {/* The family first, and labelled. "The person who booked this
                    also books for them" is the strongest hint on the screen, and
                    in the sibling case it is the answer. */}
                {family.length > 0 && (
                  <optgroup label={`Booked by ${account}`}>
                    {family.map((candidate) => (
                      <option key={candidate.id} value={candidate.id}>
                        {candidate.fullName}
                      </option>
                    ))}
                  </optgroup>
                )}
                {/* Above the long list, not below it.
                    It was below it first, which on an owner's screen put it
                    under all 176 students — present, findable only by scrolling
                    the whole school, and therefore missing in every way that
                    counts. It belongs beside the family, because "one of these
                    two, or a third we have not met" is one question. */}
                <option value={NEW}>Somebody else — add them</option>
                {rest.length > 0 && (
                  <optgroup label={isOwner(me) ? "Everybody else" : "Your students"}>
                    {rest.map((candidate) => (
                      <option key={candidate.id} value={candidate.id}>
                        {candidate.fullName}
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
            </label>

            {addingSomebodyNew && (
              <>
              <p className="small notice">
                This creates a separate student record.
                {hasStudent && <> Other past lessons stay on {choices.booking.studentName}&rsquo;s record.
                  If this is the same student with the wrong name, choose &ldquo;Same student&rdquo; above.</>}
              </p>
              <label style={{ display: "block", marginTop: 12 }}>
                <span className="small muted">Their name</span>
                <input
                  disabled={saving}
                  value={newName}
                  onChange={(e) => {
                    setNewName(e.target.value);
                    setAskAnyway(null);
                  }}
                  placeholder="As it should appear on their record"
                  style={{ display: "block", marginTop: 4, width: "100%", maxWidth: 320 }}
                />
                {/* Two different things happen depending on what the booking
                    carries, and which one it is matters enough to say before
                    the name is typed rather than after it is saved. */}
                {choices.addNew?.address ? (
                  <span className="small muted">
                    They are added to {account}&rsquo;s account, so the family sees them alongside
                    their brother or sister. You are not asked for an address — the lesson already
                    carries one.
                  </span>
                ) : (
                  <span className="small muted">
                    This lesson carries no email address, so the record will have no contact
                    details — the name will be right everywhere, but their lesson notes will not
                    reach anybody until an owner adds an address.
                  </span>
                )}
              </label>
              </>
            )}

            {/* A question rather than a refusal: Theo beside Theodore is a
                nickname as often as it is a second child. */}
            {askAnyway && (
              <div className="notice" style={{ marginTop: 12 }}>
                <p className="small">{askAnyway}</p>
                <p className="small muted">
                  If it is, close this and choose them from the list instead. If it is somebody
                  else, press the button again and they will be added.
                </p>
              </div>
            )}

            {/* Offered only when there is in fact a series. A radio pair whose
                two options do the same thing is a question that should not have
                been asked. */}
            {series && series.count > 1 && (
              <fieldset disabled={saving} style={{ border: 0, padding: 0, margin: "14px 0 0" }}>
                <legend className="small muted">How much of it</legend>
                <label className="row" style={{ marginTop: 6 }}>
                  <input
                    type="radio"
                    name={`${choiceGroup}-scope`}
                    checked={scope === "one"}
                    onChange={() => setScope("one")}
                  />
                  <span className="small">Just this lesson</span>
                </label>
                <label className="row" style={{ marginTop: 4 }}>
                  <input
                    type="radio"
                    name={`${choiceGroup}-scope`}
                    checked={scope === "series"}
                    onChange={() => setScope("series")}
                  />
                  <span className="small">
                    This lesson and the rest of {series.label} — {series.count} in all
                  </span>
                </label>
              </fieldset>
            )}

            <div className="row" style={{ marginTop: 14 }}>
              <button
                type="button"
                disabled={
                  saving || studentId === "" || (addingSomebodyNew && newName.trim() === "")
                }
                onClick={() => void move()}
              >
                {saving
                  ? "Saving…"
                  : askAnyway
                    ? "Add them anyway"
                    : addingSomebodyNew
                      ? embedded ? "Add student and match lesson" : "Add them and move the lesson"
                      : embedded ? "Match lesson" : "Move the lesson"}
              </button>
              <span className="small muted">
                Notes follow the lessons you move. Other past lessons stay where they are.
              </span>
            </div>
          </>}
        </>
      )}
    </div>
  );
}
